import WidgetHeader from '@/components/common/WidgetHeader';
import RealizedGainLossTab from '@/modules/portfolio/tables/realizedGainLoss';
import UnrealizedGainLossTab from '@/modules/portfolio/tables/unrealizedGainLoss';
import {
  Divider,
  Stack,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import RealizedGainLossSummary from './RealizedGainLossSummary';
import UnrealizedGainLossSummary from './UnrealizedGainLossSummary';

import DateRange from '@/components/date/DateRange';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useLocalization } from '@/hooks/useLocalization';
import { useOutletContext } from 'react-router';
import { usePageDefaults } from '@/hooks/usePageDefaults';
import { useExplicitPageState } from '@/hooks/useExplicitPageState';
import { GainLossGroupByMode } from './constants';
import { GroupBySelect, GroupOption } from '@/components/common/GroupBySelect';
import { ContentContainer } from '@/widgets/contentContainer';
import { tabSelectDropdownStyling } from '@/constants/styles';

interface GainLossProps extends StandardWidgetProps {
  holdingId?: number;
  accountId?: number;
  investorId?: number;
  hideTitle?: boolean;
}

interface GainLossGroupOption extends GroupOption {
  value: GainLossGroupByMode;
}

// Placeholder view option for pages without view mode functionality
const NO_VIEW_OPTIONS = ['none'];

const GainLoss = ({
  isWidget = false,
  startDate: propStartDate,
  endDate: propEndDate,
  holdingId,
  accountId,
  investorId,
  hideTitle = false,
  container = 'page',
}: GainLossProps) => {
  type Tabs = 'unrealized' | 'realized';

  const [tab, setTab] = useState<Tabs>('unrealized');
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get firm-configured default grouping from menu settings
  const { defaultGroup: firmDefaultGroup } = usePageDefaults(
    '/Portfolio/GainsAndLosses',
    'taxlots',
  );

  // Grouping options
  const GROUP_OPTIONS: GainLossGroupOption[] = useMemo(
    () => [
      { value: GainLossGroupByMode.NoGroup, label: t('none') },
      { value: GainLossGroupByMode.ByHolding, label: t('holding') },
      { value: GainLossGroupByMode.ByAccount, label: t('account') },
      { value: GainLossGroupByMode.ByTerm, label: t('grid.term') },
      { value: GainLossGroupByMode.ByGainLoss, label: t('gainOrLoss') },
      {
        value: GainLossGroupByMode.ByAssetAllocation,
        label: t('Asset Allocation'),
      },
    ],
    [t],
  );

  // Use explicit page state for grouping
  // Changes are session-only until explicitly saved
  const {
    currentGroup: activeGroup,
    setCurrentGroup: setActiveGroup,
    savedGroupValue,
    hasUnsavedGroupChange,
    isUsingFirmDefaultGroup,
    saveGroupAsDefault,
    resetGroupToFirmDefault,
  } = useExplicitPageState({
    pageKey: 'GainsLosses',
    groupOptions: GROUP_OPTIONS,
    viewOptions: NO_VIEW_OPTIONS,
    firmDefaultGroup,
    getGroupValue: (g) => g.value,
    getViewValue: (v) => v,
  });

  const handleGroupChange = (option: GainLossGroupOption) => {
    setActiveGroup(option);
  };

  const TABS = [
    { label: t('unrealizedGainsLosses'), value: 'unrealized' },
    { label: t('realizedGainsLosses'), value: 'realized' },
  ];

  const handleTabSelectChange = (event: SelectChangeEvent) => {
    setTab(event.target.value as Tabs);
  };
  // Get outlet context for dates (if provided by parent)
  const outletContext = useOutletContext<
    | {
        startDate?: string;
        endDate?: string;
        outletSetShowDateRange: (show: boolean) => void;
      }
    | undefined
  >();
  const outletStartDate = outletContext?.startDate;
  const outletEndDate = outletContext?.endDate;

  // Get default date range for the current year
  const { startDate: defaultStartDate, endDate: defaultEndDate } =
    getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

  const [startDate, setStartDate] = useState<string | undefined>(
    outletStartDate ?? propStartDate ?? defaultStartDate,
  );
  const [endDate, setEndDate] = useState<string | undefined>(
    outletEndDate ?? propEndDate ?? defaultEndDate,
  );

  // Keep local state in sync when the page-level date range (outlet) or parent props change.
  // Without this, the "By Holdings (date - date)" label and queries won't update when the
  // selector changes after initial mount.
  useEffect(() => {
    if (typeof outletStartDate !== 'undefined') {
      setStartDate(outletStartDate);
    } else if (typeof propStartDate !== 'undefined') {
      setStartDate(propStartDate);
    }
  }, [outletStartDate, propStartDate]);

  useEffect(() => {
    if (typeof outletEndDate !== 'undefined') {
      setEndDate(outletEndDate);
    } else if (typeof propEndDate !== 'undefined') {
      setEndDate(propEndDate);
    }
  }, [outletEndDate, propEndDate]);

  const handleHeaderDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Update parent layout (show date range in header) in an effect to avoid setState-during-render
  const outletSetShowDateRange = outletContext?.outletSetShowDateRange;

  /** Portfolio page uses layout header dates; keep inline picker only for widget/embed contexts. */
  const showRealizedInlineDateRange =
    tab === 'realized' && (isWidget || !outletSetShowDateRange);

  useEffect(() => {
    outletSetShowDateRange?.(true);

    return () => {
      outletSetShowDateRange?.(false);
    };
  }, [outletSetShowDateRange]);

  return (
    <ContentContainer container={container}>
      <Box
        sx={{ gap: 4, display: 'flex', flexDirection: 'column', flexGrow: 1 }}
      >
        <WidgetHeader
          title={hideTitle ? '' : t('gainsLosses')}
          leftContent={
            <Box sx={{ marginTop: 3, display: 'block' }}>
              <GroupBySelect
                label={t('groupBy')}
                options={GROUP_OPTIONS}
                activeOption={activeGroup}
                onOptionChange={handleGroupChange}
                savedGroupValue={savedGroupValue}
                firmDefaultGroup={firmDefaultGroup}
                hasUnsavedGroupChange={hasUnsavedGroupChange}
                isUsingFirmDefaultGroup={isUsingFirmDefaultGroup}
                onSaveAsDefault={saveGroupAsDefault}
                onResetToFirmDefault={resetGroupToFirmDefault}
                minWidth={120}
              />
            </Box>
          }
          rightContent={
            showRealizedInlineDateRange ? (
              <DateRange
                startDate={startDate}
                endDate={endDate}
                onChange={handleHeaderDateChange}
                shortcutsFilter={(f) =>
                  [
                    DataRangeFilterEnum.OpenYear,
                    DataRangeFilterEnum.Trailing3Months,
                    DataRangeFilterEnum.Trailing6Months,
                    DataRangeFilterEnum.Trailing1Year,
                    DataRangeFilterEnum.Trailing3Years,
                    DataRangeFilterEnum.Trailing5Years,
                    DataRangeFilterEnum.OpenQuarter,
                    DataRangeFilterEnum.ClosedQuarter,
                    DataRangeFilterEnum.ClosedYear,
                  ].includes(f)
                }
              />
            ) : null
          }
        />
        {/* Tabs - Show as dropdown on mobile, inline tabs on desktop */}
        <Box>
          {isMobile ? (
            <FormControl fullWidth>
              <Select
                labelId='gain-loss-tabs-select-label'
                id='gain-loss-tabs-select'
                value={tab}
                onChange={handleTabSelectChange}
                sx={tabSelectDropdownStyling}
              >
                {TABS.map(({ label, value }) => (
                  <MenuItem
                    key={value}
                    value={value}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Tabs
              value={tab}
              onChange={(_e, value: Tabs) => {
                setTab(value);
              }}
              variant='scrollable'
              scrollButtons='auto'
            >
              {TABS.map(({ label, value }) => (
                <Tab
                  key={value}
                  value={value}
                  label={label}
                />
              ))}
            </Tabs>
          )}
        </Box>

        <Box>
          <Stack
            direction='column'
            gap={2}
          >
            <Stack
              direction='column'
              gap={2}
            >
              {tab === 'unrealized' ? (
                <UnrealizedGainLossSummary
                  endDate={endDate}
                  holdingId={holdingId}
                  accountId={accountId}
                  investorId={investorId}
                />
              ) : (
                <RealizedGainLossSummary
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  holdingId={holdingId}
                  accountId={accountId}
                  investorId={investorId}
                />
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box>
              {tab === 'unrealized' ? (
                <UnrealizedGainLossTab
                  isWidget={isWidget}
                  holdingId={holdingId}
                  accountId={accountId}
                  investorId={investorId}
                  groupBy={activeGroup.value}
                />
              ) : (
                <RealizedGainLossTab
                  startDate={startDate}
                  endDate={endDate}
                  holdingId={holdingId}
                  accountId={accountId}
                  isWidget={isWidget}
                  investorId={investorId}
                  groupBy={activeGroup.value}
                />
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    </ContentContainer>
  );
};

export default GainLoss;
